import { Input, Select } from 'antd'
import moment from 'moment'
import { ComponentProps, useEffect, useRef, useState } from 'react'

import { useContainerSize } from '@/hooks/useContainerSize'
import { getFilterOption } from '@/utils/getFilterOption'
import cn from '@/utils/tailwind/cn'

import BaseField from '../BaseField'
import { IndependentDatetimePicker } from '.'
import {
  generateDays,
  generateYears,
  getMonths,
} from './independentDateTimePickerUtils'

type Props = ComponentProps<typeof IndependentDatetimePicker>

/**
 *
 * @initialValue format: YYYY-MM-DD
 * @returns
 */
export const RawIndependentDatePicker = ({
  id,
  name,
  onBlur,
  initialValue,
  value,
  onChange,
  minDate,
  maxDate,
  disabled,
  placeholder,
  className,
  readonly,
  showPastOnly,
  showFutureOnly,
  fatherWidth,
  ...props
}: Props) => {
  const dropdownRef = useRef<HTMLDivElement>(null)

  const years = generateYears(showPastOnly, showFutureOnly)
  const months = getMonths()

  const { width, containerRef } = useContainerSize()

  const getInitialDay = (initialValue?: Props['initialValue']) => {
    const receivedValue = initialValue || value
    const momentInitialValue = moment(receivedValue, 'YYYY-MM-DD')
    if (receivedValue) {
      return (
        generateDays(
          momentInitialValue.month() + 1,
          String(momentInitialValue.year())
        ).findIndex((item) => Number(item) === momentInitialValue.date()) + 1
      )
    }
  }

  const getInitialMonth = (initialValue?: Props['initialValue']) => {
    const receivedValue = initialValue || value
    const momentInitialValue = moment(receivedValue, 'YYYY-MM-DD')

    if (receivedValue) {
      return momentInitialValue.month() + 1
    }
  }

  const getInitialYear = (initialValue?: Props['initialValue']) => {
    const receivedValue = initialValue || value
    const momentInitialValue = moment(receivedValue, 'YYYY-MM-DD')

    if (receivedValue) {
      return years.findIndex((item) => {
        return Number(item) === momentInitialValue.year()
      })
    }
  }

  const [selectedMonth, setSelectedMonth] = useState(() =>
    getInitialMonth(initialValue || value)
  )
  const [selectedYear, setSelectedYear] = useState(() =>
    getInitialYear(initialValue || value)
  )
  const [selectedDay, setSelectedDay] = useState(() =>
    getInitialDay(initialValue || value)
  )

  const handleMonthStop = (currentValue: number) => {
    setSelectedMonth(Number(currentValue) + 1)
  }

  const handleYearStop = (currentValue: number) => {
    setSelectedYear(Number(currentValue))
  }

  const handleDayStop = (currentValue: number) => {
    setSelectedDay(Number(currentValue) + 1)
  }

  const days = generateDays(Number(selectedMonth), years[selectedYear || 0])

  const handleDropdownOpen = (open: boolean) => {
    setTimeout(() => {
      if (open && dropdownRef.current && !selectedYear) {
        const currentYear = moment().year()

        setTimeout(() => {
          const scrollContainer = dropdownRef.current?.querySelector(
            '.rc-virtual-list-holder'
          )
          const targetOption = dropdownRef.current?.querySelector(
            `.ant-select-item[title="${currentYear}"]`
          ) as unknown as HTMLElement | null

          if (scrollContainer && targetOption) {
            const scrollToPosition = targetOption.offsetTop
            requestAnimationFrame(() => {
              scrollContainer.scrollTo({
                top: scrollToPosition,
                behavior: 'auto',
              })
            })
          }
        }, 150)
      }
    }, 5)
  }

  const hasMonth = selectedMonth !== undefined && selectedMonth >= 0
  const hasDay = selectedDay !== undefined && selectedDay >= 0
  const hasYear = selectedYear !== undefined && selectedYear >= 0

  let readOnlyValue = undefined

  if (hasMonth && hasDay && hasYear) {
    const day = days[selectedDay - 1]
    const year = years[selectedYear]
    const month = String(selectedMonth).padStart(2, '0')
    readOnlyValue = [month, day, year].join('/')
  }

  const currentWidth = fatherWidth || width

  const isSmall = currentWidth < 275
  const isSmaller = currentWidth <= 160
  let monthLabel = undefined

  if (hasMonth) {
    monthLabel = isSmall
      ? String(selectedMonth).padStart(2, '0')
      : months[selectedMonth - 1]
  }

  let monthOptions = months.map((month, index) => ({
    label: month,
    value: String(index),
  }))

  if (isSmall) {
    monthOptions = months.map((_, index) => ({
      label: String(index + 1).padStart(2, '0'),
      value: String(index),
    }))
  }

  useEffect(() => {
    if (
      selectedMonth !== undefined &&
      selectedDay !== undefined &&
      selectedYear !== undefined
    ) {
      const date = moment(
        `${years[selectedYear]}-${selectedMonth}-${selectedDay}`,
        'YYYY-MM-DD'
      )

      if (date.isValid()) {
        const formattedDate = date.format('YYYY-MM-DD')
        onChange?.(formattedDate)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedDay, selectedYear])

  return (
    <BaseField {...props}>
      {readonly ? (
        <Input
          readOnly
          className="pointer-events-none"
          placeholder="MM/DD/YYYY"
          value={readOnlyValue}
        />
      ) : (
        <div
          className={cn(
            'grid w-full grid-cols-[minmax(70px,1.7fr)_minmax(60px,0.7fr)_minmax(90px,1fr)] gap-2',
            { 'grid-cols-1 grid-rows-3': isSmaller },
            className
          )}
          ref={containerRef}
        >
          <Select
            showSearch
            disabled={disabled}
            filterOption={(input, option) => getFilterOption(input, option)}
            id={id || `${name}_month`}
            key={months.length}
            onBlur={onBlur}
            onChange={handleMonthStop}
            options={monthOptions}
            placeholder="Month"
            value={
              (hasMonth
                ? { label: monthLabel, value: selectedMonth }
                : undefined) as unknown as number
            }
          />
          <Select
            showSearch
            disabled={disabled}
            filterOption={(input, option) => getFilterOption(input, option)}
            id={id || `${name}_day`}
            onBlur={onBlur}
            onChange={handleDayStop}
            options={days.map((day, index) => ({
              label: day,
              value: String(index),
            }))}
            placeholder="Day"
            value={
              (hasDay
                ? { label: days[selectedDay - 1], value: selectedDay }
                : undefined) as unknown as number
            }
          />
          <Select
            showSearch
            disabled={disabled}
            dropdownRender={(menu) => <div ref={dropdownRef}>{menu}</div>}
            filterOption={(input, option) => getFilterOption(input, option)}
            id={id || `${name}_year`}
            onBlur={onBlur}
            onChange={handleYearStop}
            onDropdownVisibleChange={handleDropdownOpen}
            options={years.map((year, index) => ({
              label: year,
              value: String(index),
            }))}
            placeholder="Year"
            popupClassName="my-dropdown"
            value={
              (hasYear
                ? { label: years[selectedYear], value: selectedYear }
                : undefined) as unknown as number
            }
            virtual={false}
          />
        </div>
      )}
    </BaseField>
  )
}
